import React, { createContext, useEffect, useRef } from "react";

import { ALL_CADENCES, CLOCK_CADENCE } from "./clock-cadences";
import { wallClockService } from "./wall-clock-service";

/**
 * Calculates the time until the next wall-clock boundary (e.g. the :05, :10 mark)
 * rather than firing N ms from now, so all tabs and page loads tick in sync.
 */
function msUntilNextBoundary(intervalMs: number): number {
    return intervalMs - (Date.now() % intervalMs);
}

/** Signals to child hooks that a provider is mounted. Used only in dev for boundary enforcement. */
export const WallClockMountedContext = createContext(false);

export function WallClockProvider({ children }: { children: React.ReactNode }) {
    const timeoutsRef = useRef<Map<CLOCK_CADENCE, ReturnType<typeof setTimeout>>>(new Map());

    useEffect(() => {
        wallClockService.notifyMounted();

        function clearCadence(cadence: CLOCK_CADENCE) {
            const recursiveTimeout = timeoutsRef.current.get(cadence);
            if (recursiveTimeout === undefined) return;
            clearTimeout(recursiveTimeout);
            timeoutsRef.current.delete(cadence);
        }

        /**
         * The only thing that starts or stops a cadence timer. Re-reads the listeners rather than trusting
         * the caller, because a tick can unsubscribe one before the re-arm below. Recursive setTimeout so
         * each callback re-aligns to the next boundary.
         */
        function syncCadenceTimer(cadence: CLOCK_CADENCE) {
            clearCadence(cadence);
            if (!wallClockService.hasListeners(cadence)) return;

            const recursiveTimeout = setTimeout(() => {
                wallClockService.tick(cadence);
                syncCadenceTimer(cadence);
            }, msUntilNextBoundary(cadence));

            timeoutsRef.current.set(cadence, recursiveTimeout);
        }

        /** Hidden tabs get throttled or frozen, so flush before re-aligning. */
        function onVisibilityChange() {
            if (document.visibilityState !== "visible") return;
            ALL_CADENCES.forEach((cadence) => {
                wallClockService.tick(cadence);
                syncCadenceTimer(cadence);
            });
        }

        const unregisterActivityHandler = wallClockService.setCadenceActivityHandler(syncCadenceTimer);

        // Children subscribe before this effect runs, so some cadences are already watched.
        ALL_CADENCES.forEach(syncCadenceTimer);
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            unregisterActivityHandler();
            ALL_CADENCES.forEach(clearCadence);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            wallClockService.notifyUnmounted();
        };
    }, []);

    return <WallClockMountedContext.Provider value={true}>{children}</WallClockMountedContext.Provider>;
}
